import { syncGoogleAnalyticsCollection } from "@/lib/analytics/trackingConsent";

export type CookieConsent = {
  necessary: true;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
};

export const DEFAULT_COOKIE_CONSENT: CookieConsent = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

export const COOKIE_ANALYTICS_WITHDRAWING_EVENT = "sa:cookie-analytics-withdrawing";
export const COOKIE_CONSENT_UPDATED_EVENT = "sa:cookie-consent-updated";

const STORAGE_KEY = "sa_cookie_consent";
// A failed write must not resurrect an older stored grant. Keep a document-local
// denial until a decision can be persisted successfully.
let sessionConsent: CookieConsent | null = null;

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function parseConsent(value: string | null): CookieConsent | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<CookieConsent>;
    if (parsed.necessary !== true) return null;
    if (!isBoolean(parsed.preferences)) return null;
    if (!isBoolean(parsed.analytics)) return null;
    if (!isBoolean(parsed.marketing)) return null;
    return {
      necessary: true,
      preferences: parsed.preferences,
      analytics: parsed.analytics,
      marketing: parsed.marketing,
    };
  } catch {
    return null;
  }
}

export function getCookieConsent(): CookieConsent {
  if (typeof window === "undefined") return DEFAULT_COOKIE_CONSENT;
  if (sessionConsent) return sessionConsent;
  try {
    return parseConsent(window.localStorage.getItem(STORAGE_KEY)) ?? DEFAULT_COOKIE_CONSENT;
  } catch {
    return DEFAULT_COOKIE_CONSENT;
  }
}

export function hasStoredCookieConsent(): boolean {
  if (typeof window === "undefined") return false;
  if (sessionConsent) return true;
  try {
    return parseConsent(window.localStorage.getItem(STORAGE_KEY)) !== null;
  } catch {
    return false;
  }
}

export function setCookieConsent(consent: CookieConsent): CookieConsent {
  if (typeof window === "undefined") return DEFAULT_COOKIE_CONSENT;
  if (!consent.analytics) {
    syncGoogleAnalyticsCollection(false);
    window.dispatchEvent(new Event(COOKIE_ANALYTICS_WITHDRAWING_EVENT));
  }
  let effective = consent;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    sessionConsent = null;
  } catch {
    effective = { ...DEFAULT_COOKIE_CONSENT };
    sessionConsent = effective;
    syncGoogleAnalyticsCollection(false);
    window.dispatchEvent(new Event(COOKIE_ANALYTICS_WITHDRAWING_EVENT));
  }
  syncGoogleAnalyticsCollection(effective.analytics);
  window.dispatchEvent(new Event(COOKIE_CONSENT_UPDATED_EVENT));
  return effective;
}
